<template>
    <div class="d-flex flex-column h-100">
        <b-form-group
            :state="states.title"
            :label="$t('keyResult.keyResult')"
            :invalid-feedback="$t('form.validations.required')"
        >
            <b-form-input
                :state="states.title"
                v-model="$v.form.title.$model"
                :placeholder="$t('keyResult.whatAreYourSuccessMetrics')"
            />
        </b-form-group>
        <Description
            :state="states.description"
            :label="$t('form.label.description')"
            :value.sync="$v.form.description.$model"
        />
        <b-row>
            <b-col>
                <Metric v-model="$v.form.metric.$model" :state="states.metric" @default-value="resetFormState" />
            </b-col>
            <b-col>
                <Responsible
                    :alreadySelectedUserId="responsible.value"
                    @already-selected-user="saveAlreadySelectedUserInForm($event)"
                    :state="states.responsible"
                    :value.sync="form.responsible"
                    :label="$t('form.label.owner')"
                >
                    <template #validations>
                        <span v-if="!$v.form.responsible.required">
                            {{ $t('form.validations.required') }}
                        </span>
                    </template>
                </Responsible>
            </b-col>
        </b-row>
        <b-row>
            <b-col :key="field.key" v-for="field in numericFields">
                <b-form-group
                    :label="field.label"
                    :state="states[field.key]"
                    :invalid-feedback="$t('form.validations.required')"
                >
                    <b-input-group
                        :prepend="form.metric ? form.metric.prepend : null"
                        :append="form.metric ? form.metric.append : null"
                    >
                        <b-form-input
                            type="number"
                            :disabled="metricIsBinary"
                            :state="states[field.key]"
                            v-model="$v.form[field.key].$model"
                            :step="form.metric ? form.metric.decimals : 0"
                        />
                    </b-input-group>
                </b-form-group>
            </b-col>
        </b-row>
        <div class="ml-auto mt-auto">
            <b-button class="mr-2" variant="outline-primary" :disabled="isSubmitting" @click="onClickCancel">
                {{ $t('form.button.cancel') }}
            </b-button>
            <button-loader variant="primary" :disabled="isSubmitting" :loader="isSubmitting" @click="onClickSubmit">
                {{ $t('form.button.save') }}
            </button-loader>
        </div>
    </div>
</template>
<script>
import Metric from './Metric'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import ButtonLoader from '@/views/components/button-loader'
import { Description, Users as Responsible } from '@/views/components/fields'

export default {
    mixins: [validationMixin],
    components: {
        Metric,
        Description,
        Responsible,
        ButtonLoader,
    },
    props: {
        isSubmitting: {
            type: Boolean,
            required: true,
        },
        responsible: {
            type: Object,
            default: null,
        },
    },
    validations: {
        form: {
            description: {},
            metric: {
                required,
            },
            startValue: {
                required,
            },
            targetValue: {
                required,
            },
            title: {
                required,
            },
            responsible: {
                required,
            },
            objectiveId: {
                required,
            },
        },
    },
    data() {
        return {
            form: {
                title: '',
                description: '',
                metric: null,
                startValue: 0,
                targetValue: 1,
                responsible: null,
                objectiveId: this.$route.query.objectiveId,
            },
        }
    },
    computed: {
        states() {
            return {
                title: this.checkFieldState('title'),
                description: this.checkFieldState('description'),
                metric: this.checkFieldState('metric'),
                startValue: this.checkFieldState('startValue'),
                targetValue: this.checkFieldState('targetValue'),
                responsible: this.checkFieldState('responsible'),
            }
        },
        numericFields() {
            return [
                {
                    key: 'startValue',
                    label: this.$t('form.label.initialValue'),
                },
                {
                    key: 'targetValue',
                    label: this.$t('feed.fields.target_value'),
                },
            ]
        },
        metricIsBinary() {
            if (!this.form.metric) {
                return false
            }

            return this.form.metric.value === 'binary'
        },
    },
    watch: {
        'form.metric': function (newMetric) {
            if (!newMetric) {
                return
            }

            if (newMetric.value !== 'binary') {
                return
            }

            this.form.startValue = 0
            this.form.targetValue = 1
        },
        'form.responsible': function (newValue) {
            return newValue
        },
        '$route.query.objectiveId': function (newObjectiveId) {
            this.form.objectiveId = newObjectiveId
        },
        responsible: function (newValue) {
            this.form.responsible = newValue
        },
    },
    mounted() {
        this.setFormDefaultData()
    },
    methods: {
        onClickSubmit() {
            if (!this.formIsValid()) {
                return
            }

            const form = this.getParsedForm()
            this.$emit('submit', form)
        },
        formIsValid() {
            this.$v.$touch()
            return this.$v.$invalid === false
        },
        getParsedForm() {
            return {
                title: this.form.title,
                description: this.form.description,
                metric: this.form.metric.value,
                start_value: this.form.startValue,
                target_value: this.form.targetValue,
                user_id: this.form.responsible.value,
                objective_id: this.form.objectiveId,
            }
        },
        onClickCancel() {
            this.$emit('cancel')
        },
        checkFieldState(key) {
            const field = this.$v.form[key]
            return field.$dirty ? !field.$invalid : null
        },
        resetFormData() {
            this.setFormDefaultData()
            this.$nextTick(() => this.resetFormState())
        },
        setFormDefaultData() {
            this.form = {
                title: '',
                description: '',
                metric: null,
                startValue: 0,
                targetValue: 1,
                responsible: null,
                objectiveId: this.$route.query.objectiveId,
            }
        },
        resetFormState() {
            this.$v.$reset()
            this.setFormDefaultData()
        },
        saveAlreadySelectedUserInForm(user) {
            this.$v.form.responsible.$model = user
        },
    },
}
</script>
<style lang="scss" scoped>
.label-style {
    color: #3b3f4e;
    font-size: 16px;
    font-weight: 700;
}

::v-deep legend {
    @extend .label-style;
}

::v-deep label {
    @extend .label-style;
}
</style>
